import {Injectable} from '@angular/core';
import {BehaviorSubject, EMPTY, Observable, retry, Subscription, switchMap, take, throwError} from 'rxjs';
import {ConfigService} from '../../../config.service';
import {catchError, tap} from 'rxjs/operators';
import {HttpClient} from '@angular/common/http';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {environment} from '../../../../environments/environment';
import {differenceInSeconds} from 'date-fns';

@UntilDestroy()
@Injectable()
export class QrCodeAssociationService {
  private readonly WAIT_SECONDS_FOR_NEW_REQUEST = 15_000;

  private _qrCode$ = new BehaviorSubject<string>(undefined);
  readonly qrCode$ = this._qrCode$.asObservable();

  get qrCode(): string {
    return this._qrCode$.getValue();
  }

  private _deviceAssociated$ = new BehaviorSubject<boolean>(false);
  readonly deviceAssociated$ = this._deviceAssociated$.asObservable();

  get deviceAssociated(): boolean {
    return this._deviceAssociated$.getValue();
  }

  readonly pollingWaitTimeInMS = ConfigService.settings.pollingTimeMs;

  constructor(
    private http: HttpClient
  ) {
  }

  private triggerObservable(startTime: number, waitTime: number, observable: Observable<any>): void {
    const endTime = Date.now();
    const secondsElapsed = differenceInSeconds(endTime, startTime) * 1000;
    setTimeout(() => observable.subscribe(), secondsElapsed > waitTime ? 0 : waitTime - secondsElapsed);
  }

  createQrCode(): Subscription {
    const url = `${ConfigService.settings.baseUrl}/enroll`;
    return this.http.get<QrCodeResponse>(`${url}`, {withCredentials: true}).pipe(
      take(1),
      untilDestroyed(this),
      tap(response => this._qrCode$.next(response.qrcode)),
      switchMap(response => this.verifyQrCode$(response.request_id))
    ).subscribe();
  }

  verifyQrCode$(requestId: string): Observable<any> {
    const url = `${environment.OtpGwBaseUrl}/events/${requestId}`;
    const startTime = Date.now();
    return this.http.get<Event>(url).pipe(
      untilDestroyed(this),
      retry({delay: this.pollingWaitTimeInMS}),
      take(1),
      switchMap(response => {
        const result = response.data.result;
        if (result === DataResult.Success) {
          this._deviceAssociated$.next(true);
        } else if (result === DataResult.QrCodeTimeout) {
          this.createQrCode();
        } else if (result === DataResult.Failure) {
          return throwError(() => Error('Qr code not verified'));
        }
        return EMPTY;
      }),
      catchError(err => {
        try {
          const statusCode = err.error.meta.status;
          if (statusCode === 504) {
            this.verifyQrCode$(requestId).subscribe();
          } else if (statusCode === 404) {
            this.createQrCode();
          } else {
            this.triggerObservable(startTime, this.WAIT_SECONDS_FOR_NEW_REQUEST, this.verifyQrCode$(requestId));
          }
        } catch (e) {
          this.triggerObservable(startTime, this.WAIT_SECONDS_FOR_NEW_REQUEST, this.verifyQrCode$(requestId));
        }
        return EMPTY;
      })
    );
  }

  private modifyOtpDeliveryMethod$(otpDeliveryMethod: OtpDeliveryMethod, enabled: boolean, showLoginPopup: boolean): Observable<any> {
    const url = `${ConfigService.settings.baseUrl}/otp_delivery_method`;
    const payload = {
      otp_delivery_method: otpDeliveryMethod,
      enabled,
      show_login_popup: showLoginPopup
    };
    return this.http.post(url, payload, {withCredentials: true}).pipe(take(1));
  }
}

export interface QrCodeResponse {
  qrcode: string;
  request_id: string;
}

export interface Event {
  meta: Meta;
  data: Data;
}

export interface Meta {
  status: number;
  message: string;
}

export interface Data {
  request_id: string;
  result: DataResult;
  user_id: string;
  token: string;
}

export type DataResult = 'SUCCESS' | 'FAILURE' | 'QRCODE_TIMEOUT';
export const DataResult = {
  Success: 'SUCCESS' as DataResult,
  Failure: 'FAILURE' as DataResult,
  QrCodeTimeout: 'QRCODE_TIMEOUT'
};

export type OtpDeliveryMethod = 'SMS' | 'APP';
export const OtpDeliveryMethod = {
    Sms: 'SMS' as OtpDeliveryMethod,
    App: 'APP' as OtpDeliveryMethod,
};
